// The request fields the job types share (spec F9, section 6): the Studio's common fields, a
// video's, the source as a list and a video's elements. Each type module builds its body from them.
import type { AdvancedSettings, Shot } from "../../../api/generated/model";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import type { GenerateDraft } from "../draft-state";
import { elementOf } from "../video/elements";

/** The fields every Studio request has. */
export function commonFields(draft: GenerateDraft) {
  return {
    type: draft.type,
    model: draft.model,
    outputs: draft.outputs,
    aspect_ratio: draft.aspectRatio,
  };
}

/**
 * A video's length, resolution and sound, or with Multi-Shot on its shots, which carry the
 * prompts and the lengths (G6: exclusive with both); and the advanced settings its block takes.
 */
export function videoFields(draft: GenerateDraft, advanced: AdvancedSettings | null) {
  return {
    ...(draft.multiShot
      ? {
          resolution: draft.resolution,
          generate_audio: draft.audio,
          shots: draft.shots.map((shot) => ({
            prompt: shot.prompt.trim(),
            duration_seconds: shot.durationSeconds,
          })),
        }
      : {
          duration_seconds: draft.durationSeconds,
          resolution: draft.resolution,
          generate_audio: draft.audio,
        }),
    ...(advanced === null ? {} : { advanced }),
  };
}

/** The source as a list: a tweak's first reference. */
export function sourcesOf(draft: GenerateDraft) {
  return draft.source === null ? [] : [draft.source];
}

/** A video's elements, where the composer left them in (#229). */
export function elementsOf(draft: GenerateDraft) {
  return draft.elements.length === 0 ? {} : { elements: draft.elements.map(elementOf) };
}

/** A multi-shot start has a prompt for every shot. */
export function everyShotWritten(body: { shots?: Shot[] | null }): boolean {
  return (body.shots ?? []).every((shot) => (shot.prompt ?? "").length > 0);
}

/** A library file as a request names it. */
export function assetRefOf(picked: PickedImage): { kind: "asset"; asset_id: string } {
  return { kind: "asset", asset_id: picked.assetId };
}
